// src/services/api/shopConfigApi.js

/**
 * Shop Configuration API methods
 * @param {Object} api - axios instance
 * @returns {Object} shop configuration API methods
 */
const shopConfigApi = (api) => {
  return {
    /**
     * Fetch paginated shop configurations
     * @param {number} page - page number
     * @param {number} pageSize - items per page
     * @param {string} searchTerm - search term
     * @returns {Promise} response data
     */
    fetchShopConfigs: async (page = 1, pageSize = 10, searchTerm = "") => {
      try {
        const response = await api.get("/api/ShopConfiguration", {
          params: { page, pageSize, searchTerm },
        });
        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch shop configurations"
        );
      }
    },

    /**
     * Fetch a single shop configuration by template name
     * @param {string} templateName - template name
     * @returns {Promise} response data
     */
    fetchShopConfigByTemplate: async (templateName) => {
      try {
        const response = await api.get(
          `/api/ShopConfiguration/${templateName}`
        );
        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch shop configuration"
        );
      }
    },

    /**
     * Add a new shop configuration with strategy file
     * @param {Object} configData - shop configuration data
     * @returns {Promise} response data
     */
    addShopConfig: async (configData) => {
      try {
        // Handle file upload using FormData
        const formData = new FormData();

        // Check if strategy file is present
        if (configData.strategyFile) {
          formData.append("StrategyFile", configData.strategyFile);

          // Remove the file from the JSON to prevent circular reference
          const configDataCopy = { ...configData };
          delete configDataCopy.strategyFile;

          // Add the config as JSON
          formData.append("ConfigJson", JSON.stringify(configDataCopy));
        } else {
          // If no file, just add the config as JSON
          formData.append("ConfigJson", JSON.stringify(configData));
        }

        const response = await api.post("/api/ShopConfiguration", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to add shop configuration"
        );
      }
    },

    /**
     * Update an existing shop configuration with strategy file
     * @param {string} templateName - template name
     * @param {Object} configData - updated shop configuration data
     * @returns {Promise} response data
     */
    updateShopConfig: async (templateName, configData) => {
      try {
        // Handle file upload using FormData
        const formData = new FormData();

        // Check if strategy file is present
        if (configData.strategyFile) {
          formData.append("StrategyFile", configData.strategyFile);

          // Remove the file from the JSON to prevent circular reference
          const configDataCopy = { ...configData };
          delete configDataCopy.strategyFile;

          // Add the config as JSON
          formData.append("ConfigJson", JSON.stringify(configDataCopy));
        } else {
          // If no file, just add the config as JSON
          formData.append("ConfigJson", JSON.stringify(configData));
        }
        console.log(formData, templateName);
        const response = await api.put(
          `/api/ShopConfiguration/${templateName}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to update shop configuration"
        );
      }
    },

    /**
     * Delete a shop configuration
     * @param {string} templateName - template name
     * @returns {Promise} response data
     */
    deleteShopConfig: async (templateName) => {
      try {
        const response = await api.delete(
          `/api/ShopConfiguration/${templateName}`
        );
        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to delete shop configuration"
        );
      }
    },

    /**
     * Get strategy source code for a shop configuration
     * @param {string} templateName - template name
     * @returns {Promise} response data
     */
    getStrategySourceCode: async (templateName) => {
      try {
        const response = await api.get(
          `/api/ShopConfiguration/${templateName}/strategy`
        );
        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to fetch strategy source code"
        );
      }
    },

    /**
     * Test a strategy with OCR text
     * @param {string} templateName - template name
     * @param {string} ocrText - OCR text to test the strategy with
     * @returns {Promise} response data
     */
    testStrategy: async (templateName, ocrText) => {
      try {
        const response = await api.post(
          `/api/ShopConfiguration/${templateName}/test-strategy`,
          { ocrText }
        );
        return response.data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to test strategy"
        );
      }
    },
    /**
     * Fetch counters for autocomplete
     * @param {number} page - page number
     * @param {number} pageSize - items per page
     * @param {string} searchTerm - search term
     * @returns {Promise} response data
     */
    fetchCounters: async (page = 1, pageSize = 10, searchTerm = "") => {
      try {
        // Create params object with required pagination parameters
        const params = {
          Page: page,
          PageSize: pageSize,
        };

        // Only add SearchTerm parameter if it has a value
        if (searchTerm && searchTerm.trim() !== "") {
          params.SearchTerm = searchTerm;
        }

        const response = await api.get("/api/ShopConfiguration/counters", {
          params: params,
        });

        if (response.data && response.data.data && response.data.data.data) {
          return response.data.data.data;
        }
        return [];
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch counters"
        );
      }
    },
  };
};

export default shopConfigApi;
